import React, { useState } from "react";
import Header from "../../components/navbars/HomeHeader";
import { Button } from "../../components/Button";
import { TextField } from "../../components/form-fields/TextField";
import {
  Container,
  Heading,
  SubHeading,
  Label,
  LogoSVG,
} from "./LoginPage";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { APIs } from "../../services/Apis";
import usePageTitle from "../../hooks/usePageTitle";

export default function ForgotPasswordPage() {
  usePageTitle("Forgot Password");
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [codeSent, setCodeSent] = useState(false);

  const handleRequestCode = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    if (!email) {
      toast.error("Please enter your email.");
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await APIs.resetPass(email);
      if (response.success) {
        toast.success("Verification code sent! Check your inbox.");
        setCodeSent(true);
      } else {
        toast.error(response.message || "Failed to send verification code. Please try again.");
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    if (!code) {
      toast.error("Please enter the verification code.");
      return;
    }

    if (!password) {
      toast.error("Please enter a new password.");
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await APIs.resetPass(email, code, password);
      if (response.success) {
        toast.success("Password reset successful! Redirecting to login...");
        setTimeout(() => {
          navigate("/login");
        }, 1500);
      } else {
        toast.error(response.message || "Failed to reset password. Please try again.");
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBackToEmail = () => {
    setCodeSent(false);
    setCode("");
    setPassword("");
  };

  return (
    <>
      <Header />
      <Container className="py-10 md:py-20 flex justify-center">
        <div className="w-full max-w-md px-4 md:px-0">
          <div>
            <Heading className="mt-4 text-center lg:text-4xl">
              Reset Password
            </Heading>
            {!codeSent ? (
              <>
                <SubHeading className="mt-4 text-center">
                  Enter your email address and we'll send you a verification code
                  to reset your password.
                </SubHeading>
                <form className="mt-6 flex flex-col gap-8" onSubmit={handleRequestCode}>
                  <TextField
                    label="Email"
                    name="email"
                    type="email"
                    placeholder="youremail@yourdomain.com"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    required
                  />
                  <Button
                    variant="solid"
                    color="primary"
                    className="w-full justify-center py-4"
                    size="lg"
                    type="submit"
                    loading={isSubmitting}
                    disabled={isSubmitting}
                  >
                    Send Verification Code
                  </Button>
                </form>
              </>
            ) : (
              <>
                <SubHeading className="mt-4 text-center">
                  Enter the verification code sent to {email} and your new password
                </SubHeading>
                <form className="mt-6 flex flex-col gap-8" onSubmit={handleResetPassword}>
                  <TextField
                    label="Verification Code"
                    name="code"
                    type="text"
                    placeholder="Enter verification code"
                    value={code}
                    onChange={(event) => setCode(event.target.value)}
                    required
                  />
                  <TextField
                    label="New Password"
                    name="password"
                    type="password"
                    placeholder="Enter new password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    required
                  />
                  <Button
                    variant="solid"
                    color="primary"
                    className="w-full justify-center py-4"
                    size="lg"
                    type="submit"
                    loading={isSubmitting}
                    disabled={isSubmitting}
                  >
                    Reset Password
                  </Button>
                  <Button
                    variant="text"
                    color="slate"
                    className="w-full justify-center"
                    size="sm"
                    type="button"
                    onClick={handleBackToEmail}
                    disabled={isSubmitting}
                  >
                    Back to email
                  </Button>
                </form>
              </>
            )}
            <div className="mt-6 text-center">
              <span className="text-sm text-gray-600">
                Remember your password?{" "}
              </span>
              <Button to="/login" variant="text" color="blue" size="sm">
                Sign in
              </Button>
            </div>
          </div>
        </div>
      </Container>
    </>
  );
}

